import { maintenanceAPI } from './api';
import type { PlanMaestro, PlanMaestroResponse } from '../../types/planMaestro';

export const maintenanceService = {
    async getPlanesMaestros(type?: 'company' | 'system' | 'all') {
        const response = await maintenanceAPI.get<{ success: boolean; data: PlanMaestro[] }>('/planes-maestros', {
            params: { type }
        });
        return response.data.data;
    },
    async generarReporteEjecutivo(params?: {
        fecha_inicio?: string;
        fecha_fin?: string;
        vehiculos?: string[];
    }) {
        const response = await maintenanceAPI.get('/reportes/ejecutivo', {
            params,
            responseType: 'blob', // IMPORTANTE: para recibir el archivo
        });
        return response;
    },

    async generarReporteOrdenesTrabajo(filtros?: any) {
        const response = await maintenanceAPI.get('/reportes/ordenes-trabajo', {
            params: filtros,
            responseType: 'blob',
        });
        return response;
    },
    async generarReporteCostos(periodo?: string) {
        const response = await maintenanceAPI.get('/reportes/costos', {
            params: { periodo },
            responseType: 'blob',
        });
        return response;
    },

    async exportVehiclePlanDetailsExcel(vehiculoId: string) {
        const response = await maintenanceAPI.get(`/mantenimiento/flota/vehiculo/${vehiculoId}/plan-details/excel`, {
            responseType: 'blob',
        });
        return response;
    },

    async exportVehiclePlanDetailsPdf(vehiculoId: string) {
        const response = await maintenanceAPI.get(`/mantenimiento/flota/vehiculo/${vehiculoId}/plan-details/pdf`, {
            responseType: 'blob',
        });
        return response;
    },
    async getPlanMaestroById(id: number) {
        const response = await maintenanceAPI.get<PlanMaestroResponse>(`/planes-maestros/${id}`);
        return response.data;
    },

    async createPlanMaestro(data: Partial<PlanMaestro>) {
        const response = await maintenanceAPI.post('/planes-maestros', data);
        return response.data;
    },

    async updatePlanMaestro(id: number, data: Partial<PlanMaestro>) {
        const response = await maintenanceAPI.put(`/planes-maestros/${id}`, data);
        return response.data;
    },

    async deletePlanMaestro(id: number) {
        const response = await maintenanceAPI.delete(`/planes-maestros/${id}`);
        return response.data;
    },

    // Ítems de Catálogo
    async getItemsCatalogo(params?: any) {
        const response = await maintenanceAPI.get<{ success: boolean; data: any[] }>('/items-catalogo', { params });
        return response.data;
    },

    async createItemCatalogo(data: any) {
        const response = await maintenanceAPI.post('/items-catalogo', data);
        return response.data;
    },

    async updateItemCatalogo(id: number, data: any) {
        const response = await maintenanceAPI.put(`/items-catalogo/${id}`, data);
        return response.data;
    },

    async deleteItemCatalogo(id: number) {
        const response = await maintenanceAPI.delete(`/items-catalogo/${id}`);
        return response.data;
    },
    async deleteMultipleItemsCatalogo(ids: number[]) {
        const response = await maintenanceAPI.post('/items-catalogo/bulk-delete', { ids });
        return response.data;
    },

    async deletePlanItem(planId: number, itemId: number) {
        const response = await maintenanceAPI.delete(`/planes-maestros/${planId}/items/${itemId}`);
        return response.data;
    },

    async updatePlanItem(planId: number, itemId: number, data: any) {
        const response = await maintenanceAPI.put(`/planes-maestros/${planId}/items/${itemId}`, data);
        return response.data;
    },

    async addPlanItem(planId: number, itemCatalogoId: number) {
        const response = await maintenanceAPI.post(`/planes-maestros/${planId}/items`, { item_catalogo_id: itemCatalogoId });
        return response.data;
    },

    async assignVehicle(planId: number, vehiculoId: string) {
        const response = await maintenanceAPI.post(`/planes-maestros/${planId}/assign-vehicle`, { vehiculo_id: vehiculoId });
        return response.data;
    },

    async unassignVehicle(planId: number, vehiculoId: string) {
        const response = await maintenanceAPI.post(`/planes-maestros/${planId}/unassign-vehicle`, { vehiculo_id: vehiculoId });
        return response.data;
    },

    async getAssignedVehicles(planId: number) {
        const response = await maintenanceAPI.get(`/planes-maestros/${planId}/vehicles`);
        return response.data;
    },

    async duplicatePlanMaestro(id: number) {
        const response = await maintenanceAPI.post(`/planes-maestros/${id}/duplicate`);
        return response.data;
    },
    async getFlotaStatus() {
        const response = await maintenanceAPI.get<{ success: boolean; data: any[] }>('/mantenimiento/flota');
        return response.data;
    },

    // 🧠 GESTIÓN DE CACHÉ DE SESIÓN
    // Permite persistir datos entre navegación sin recargar del servidor cada vez,
    // pero con un tiempo de vida corto (5 min) para evitar datos obsoletos.
    _clearOldCache() {
        try {
            const cache = JSON.parse(sessionStorage.getItem('mnt_routines_cache') || '{}');
            const now = Date.now();
            const TTL = 300000; // 5 minutos en ms

            const newCache: Record<string, any> = {};
            Object.keys(cache).forEach(id => {
                if (now - cache[id].timestamp < TTL) {
                    newCache[id] = cache[id];
                }
            });
            sessionStorage.setItem('mnt_routines_cache', JSON.stringify(newCache));
        } catch (e) {
            sessionStorage.removeItem('mnt_routines_cache');
        }
    },

    clearRoutinesCache(vehiculoId?: string) {
        if (vehiculoId) {
            const cache = JSON.parse(sessionStorage.getItem('mnt_routines_cache') || '{}');
            delete cache[vehiculoId];
            sessionStorage.setItem('mnt_routines_cache', JSON.stringify(cache));
        } else {
            sessionStorage.removeItem('mnt_routines_cache');
        }
    },

    async getVehicleRoutines(vehiculoId: string, forceFetch = false) {
        // 1. Limpiar caché antigua antes de consultar
        this._clearOldCache();

        // 2. Intentar obtener de caché si no es forceFetch
        if (!forceFetch) {
            try {
                const cache = JSON.parse(sessionStorage.getItem('mnt_routines_cache') || '{}');
                if (cache[vehiculoId]) {
                    console.log(`🧠 Usando caché para vehículo ${vehiculoId}`);
                    return cache[vehiculoId].data;
                }
            } catch (e) { }
        }

        // 3. Si no hay caché o es forceFetch, ir al servidor
        const response = await maintenanceAPI.get<{ success: boolean; data: any[] }>(`/mantenimiento/vehiculos/${vehiculoId}/rutinas`);

        // 4. Guardar en caché (guardamos solo el array de data)
        const routinesData = response.data.data;

        try {
            const cache = JSON.parse(sessionStorage.getItem('mnt_routines_cache') || '{}');
            cache[vehiculoId] = {
                timestamp: Date.now(),
                data: routinesData
            };
            sessionStorage.setItem('mnt_routines_cache', JSON.stringify(cache));
        } catch (e) { }

        return routinesData;
    },

    async getFlotaRequirements() {
        const response = await maintenanceAPI.get<{ success: boolean; data: any[] }>('/mantenimiento/flota/requerimientos');
        return response.data;
    },

    async exportFlotaRequirementsExcel() {
        return maintenanceAPI.get('/mantenimiento/flota/requerimientos/excel', {
            responseType: 'blob',
        });
    },

    async exportFlotaRequirementsPdf() {
        return maintenanceAPI.get('/mantenimiento/flota/requerimientos/pdf', {
            responseType: 'blob',
        });
    },

    async getVehicleHistory(vehiculoId: string) {
        const response = await maintenanceAPI.get<{ success: boolean; data: any[]; plan?: any }>(`/mantenimiento/vehiculos/${vehiculoId}/historial`);
        return response.data;
    },

    async getOrdenesTrabajo(params?: {
        status?: string;
        search?: string;
        prioridad?: string;
        tipo?: string;
        fecha_inicio?: string;
        fecha_fin?: string;
    }) {
        const response = await maintenanceAPI.get<{ success: boolean; data: any[] }>('/ordenes-trabajo', {
            params
        });
        return response.data;
    },

    async getOrdenTrabajo(id: number) {
        const response = await maintenanceAPI.get<{ success: boolean; data: any }>(`/ordenes-trabajo/${id}`);
        return response.data;
    },

    async getAvailableItemsForOT(otId: number) {
        const response = await maintenanceAPI.get<{ success: boolean; data: any[] }>(`/ordenes-trabajo/${otId}/available-items`);
        return response.data;
    },
    // ===== IMPORTACIÓN MASIVA DE ÍTEMS =====

    async previewImportCatalogo(file: File) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await maintenanceAPI.post('/items-catalogo/import/preview', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    },

    async importItemsCatalogo(items: any[]) {
        const response = await maintenanceAPI.post('/items-catalogo/import', { items });
        return response.data;
    },

    async downloadTemplateCatalogo() {
        const response = await maintenanceAPI.get('/items-catalogo/import/template', {
            responseType: 'blob'
        });
        return response;
    },

    // ===== IMPORTACIÓN DE PLANES MAESTROS =====
    async previewImportPlanMaestro(file: File) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await maintenanceAPI.post('/planes-maestros/import/preview', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    },

    async importPlanMaestro(data: any) {
        const response = await maintenanceAPI.post('/planes-maestros/import', data);
        return response.data;
    },

    downloadTemplate: async () => {
        const response = await maintenanceAPI.get("/planes-maestros/import/template", {
            responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "plantilla_mantenimiento.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
    },
    // NUEVO: Agregar ítem a la OT
    async addItemToOT(otId: number, data: {
        item_catalogo_id: number;
        cantidad: number;
        proveedor_seleccionado_id?: number | null;
        precio_cotizado?: number;
        costo_unitario: number;
        observaciones?: string;
    }) {
        const response = await maintenanceAPI.post(`/ordenes-trabajo/${otId}/items`, data);
        return response.data;
    },

    async createOTFromRoutine(data: { vehiculo_id: string; rutina_id: number; kilometraje_actual: number }) {
        const response = await maintenanceAPI.post('/ordenes-trabajo/from-routine', data);
        return response.data;
    },

    async createOTFromMultipleRoutines(data: { vehiculo_id: string; rutinas_ids: number[]; kilometraje_actual: number }) {
        const response = await maintenanceAPI.post('/ordenes-trabajo/from-multiple-routines', data);
        return response.data;
    },

    async createCustomOT(data: {
        vehiculo_id: string;
        tipo: 'PREVENTIVO' | 'CORRECTIVO' | 'EMERGENCIA';
        prioridad: 'BAJA' | 'MEDIA' | 'ALTA' | 'URGENTE';
        fecha_programada: string;
        kilometraje_inicio: number;
        observaciones?: string;
    }) {
        const response = await maintenanceAPI.post('/ordenes-trabajo', data);
        return response.data;
    },

    async closeOT(id: number, data: {
        kilometraje_cierre: number;
        fecha_cierre: string;
        observaciones_cierre?: string;
        costo_mano_obra: number;
        taller_id?: number | null;
        mecanicos_ids?: number[];
        // NUEVOS campos
        actividades_postergar?: number[];      // ← IDs de actividades a postergar
        actividades_cancelar?: number[];       // ← IDs de actividades a cancelar
        justificaciones?: Record<number, string>; // ← Justificaciones por actividad
    }) {
        const response = await maintenanceAPI.post(`/ordenes-trabajo/${id}/close`, data);
        return response.data;
    },
    // Obtener actividades postergadas de un vehículo
    getActividadesPostergadas: async (vehiculoId: string) => {
        const response = await maintenanceAPI.get(
            `/vehiculos/${vehiculoId}/actividades-postergadas`
        );
        return response.data;
    },
    // Aplicar actividades postergadas a una OT
    aplicarActividadesPostergadas: async (otId: number, data: {
        actividades_ids: number[];
    }) => {
        const response = await maintenanceAPI.post(
            `/ordenes-trabajo/${otId}/aplicar-postergadas`,
            data
        );
        return response.data;
    },

    // Talleres
    async getTalleres() {
        const response = await maintenanceAPI.get<{ success: boolean; data: any[] }>('/talleres');
        return response.data;
    },

    async createTaller(data: any) {
        const response = await maintenanceAPI.post('/talleres', data);
        return response.data;
    },

    async updateTaller(id: number, data: any) {
        const response = await maintenanceAPI.put(`/talleres/${id}`, data);
        return response.data;
    },

    async deleteTaller(id: number) {
        const response = await maintenanceAPI.delete(`/talleres/${id}`);
        return response.data;
    },
    // ===== ALERTAS DE MANTENIMIENTO =====

    async getAlertas() {
        const response = await maintenanceAPI.get('/alertas');
        return response.data;
    },

    async getResumenAlertas() {
        const response = await maintenanceAPI.get('/alertas/resumen');
        return response.data;
    },

    async getAlertasVehiculo(vehiculoId: string) {
        const response = await maintenanceAPI.get(`/alertas/vehiculo/${vehiculoId}`);
        return response.data;
    },

    async marcarAlertaLeida(alertaId: number) {
        const response = await maintenanceAPI.put(`/alertas/${alertaId}/marcar-leida`);
        return response.data;
    },

    async marcarTodasAlertasLeidas() {
        const response = await maintenanceAPI.post('/alertas/marcar-todas-leidas');
        return response.data;
    },

    async eliminarAlerta(alertaId: number) {
        const response = await maintenanceAPI.delete(`/alertas/${alertaId}`);
        return response.data;
    },
    // Repuestos Pendientes
    async getRepuestosPendientes(params: any = {}) {
        const response = await maintenanceAPI.get<{ success: boolean; data: any[] }>('/repuestos-pendientes', { params });
        return response.data;
    },

    async createRepuestoPendiente(data: any) {
        const response = await maintenanceAPI.post('/repuestos-pendientes', data);
        return response.data;
    },

    async updateRepuestoPendiente(id: number, data: any) {
        const response = await maintenanceAPI.put(`/repuestos-pendientes/${id}`, data);
        return response.data;
    },

    async deleteRepuestoPendiente(id: number) {
        const response = await maintenanceAPI.delete(`/repuestos-pendientes/${id}`);
        return response.data;
    },
    async startOT(id: number, data?: { taller_id?: number; mecanicos_ids?: number[] }) {
        const response = await maintenanceAPI.post(`/ordenes-trabajo/${id}/start`, data || {});
        return response.data;
    },

    // Mecánicos del taller
    async getMecanicos(tallerId: number) {
        const response = await maintenanceAPI.get<{ success: boolean; data: any[] }>(`/talleres/${tallerId}/mecanicos`);
        return response.data;
    },

    async createMecanico(tallerId: number, data: any) {
        const response = await maintenanceAPI.post(`/talleres/${tallerId}/mecanicos`, data);
        return response.data;
    },

    async updateMecanico(tallerId: number, mecanicoId: number, data: any) {
        const response = await maintenanceAPI.put(`/talleres/${tallerId}/mecanicos/${mecanicoId}`, data);
        return response.data;
    },

    async deleteMecanico(tallerId: number, mecanicoId: number) {
        const response = await maintenanceAPI.delete(`/talleres/${tallerId}/mecanicos/${mecanicoId}`);
        return response.data;
    },
    // NUEVO: Actualizar actividad de OT
    updateActividadOT: async (actividadId: number, data: {
        proveedor_seleccionado_id?: number | null;
        precio_cotizado?: number;
        costo_unitario?: number;
        realizado?: boolean;
    }) => {
        const response = await maintenanceAPI.put(
            `/ordenes-trabajo/actividades/${actividadId}`,
            data
        );
        return response.data;
    },

    // NUEVO: Analizar mejores proveedores para asignación masiva
    analizarMejoresProveedores: async (otId: number, actividadIds?: number[]) => {
        const response = await maintenanceAPI.get(
            `/ordenes-trabajo/${otId}/analizar-proveedores`,
            { params: { actividades_ids: actividadIds } }
        );
        return response.data;
    },

    // NUEVO: Aplicar proveedor a múltiples items
    aplicarProveedorMasivo: async (otId: number, data: {
        proveedor_id: number;
        items_ids: number[];
        tipo?: 'INDIVIDUAL' | 'PAQUETE';
        paquete_id?: number | null;
    }) => {
        const response = await maintenanceAPI.post(
            `/ordenes-trabajo/${otId}/aplicar-proveedor-masivo`,
            {
                ...data,
                tipo: data.tipo || 'INDIVIDUAL'
            }
        );
        return response.data;
    },
};
